import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  AdminUsersApiError,
  type AdminUsersClient,
  type AdminUsersErrorCode,
  type AuditEventSummary,
  type ViewerCreationResult,
  type ViewerDirectoryItem,
  type ViewerSummary,
} from "./admin-users-api";
import {
  acquireAction,
  canShowViewerProvisioning,
  matchesViewerFilter,
  refreshViewersAfterRetryableCreateError,
  releaseAction,
  type ViewerFilter,
  viewerSecondaryAction,
} from "./admin-users-view-model";
import {
  bidiIsolationAttributes,
  useLocale,
  type MessageKey,
} from "./localization";
import { USERS_CHANGED_EVENT } from "./UserAccessPanel";

export interface AdminUsersPanelProps {
  readonly api: AdminUsersClient;
  readonly canManageUsers: boolean;
}

interface LocalizedNotice {
  readonly key: MessageKey;
  readonly nestedMessageKey?: MessageKey;
  readonly tone: "neutral" | "success" | "warning" | "error";
}

type LocalizedCopy = Omit<LocalizedNotice, "tone">;
type CreateErrorField = "email" | "form";

const ERROR_MESSAGE_KEYS = {
  protected_account: "adminUsers.error.protectedIdentity",
  access_conflict: "adminUsers.error.profileConflict",
  invalid_deletion: "adminUsers.error.request",
  user_not_found: "adminUsers.error.notFound",
  user_deletion_in_progress: "adminUsers.error.request",
  user_deletion_unavailable: "adminUsers.error.service",
  invalid_email: "adminUsers.error.invalidEmail",
  invalid_password: "adminUsers.error.invalidPassword",
  viewer_not_found: "adminUsers.error.notFound",
  protected_identity: "adminUsers.error.protectedIdentity",
  viewer_identity_conflict: "adminUsers.error.identityConflict",
  viewer_profile_conflict: "adminUsers.error.profileConflict",
  viewer_disabled: "adminUsers.error.disabled",
  viewer_provisioning_in_progress: "adminUsers.error.provisioning",
  viewer_setup_delivery_failed: "adminUsers.error.deliveryFailed",
  viewer_identity_permission_denied: "adminUsers.error.identityPermission",
  viewer_identity_unavailable: "adminUsers.error.identityUnavailable",
  viewer_profile_unavailable: "adminUsers.error.profileUnavailable",
  viewer_audit_unavailable: "adminUsers.error.auditUnavailable",
  viewer_disable_failed: "adminUsers.error.disableFailed",
  viewer_access_reset_failed: "adminUsers.error.resetFailed",
  permission_denied: "adminUsers.error.permissionDenied",
  session_expired: "adminUsers.error.sessionExpired",
  network_unavailable: "adminUsers.error.network",
  service_unavailable: "adminUsers.error.service",
  invalid_response: "adminUsers.error.invalidResponse",
  request_failed: "adminUsers.error.request",
} as const satisfies Readonly<Record<AdminUsersErrorCode, MessageKey>>;

function createErrorField(error: unknown): CreateErrorField {
  if (!(error instanceof AdminUsersApiError)) return "form";
  if (error.code === "invalid_email") return "email";
  return "form";
}

const AUDIT_ACTION_MESSAGE_KEYS: Readonly<Record<string, MessageKey>> =
  Object.freeze({
    "administration.user_deletion_started":
      "adminUsers.audit.action.userDeletionStarted",
    "administration.user_deleted": "adminUsers.audit.action.userDeleted",
    "administration.user_deletion_denied":
      "adminUsers.audit.action.userDeletionDenied",
    "administration.user_deletion_failed":
      "adminUsers.audit.action.userDeletionFailed",
    "authentication.sign_in": "adminUsers.audit.action.signIn",
    "authentication.sign_out": "adminUsers.audit.action.signOut",
    "authentication.mfa_enrolled": "adminUsers.audit.action.mfaEnrolled",
    "authentication.sign_in_denied": "adminUsers.audit.action.signInDenied",
    "administration.viewer_created": "adminUsers.audit.action.viewerCreated",
    "administration.viewer_reconciled":
      "adminUsers.audit.action.viewerReconciled",
    "administration.viewer_mfa_activated":
      "adminUsers.audit.action.mfaActivated",
    "administration.viewer_setup_resent": "adminUsers.audit.action.setupResent",
    "administration.viewer_disabled": "adminUsers.audit.action.viewerDisabled",
    "administration.viewer_access_reset": "adminUsers.audit.action.accessReset",
    "administration.viewer_management_denied":
      "adminUsers.audit.action.managementDenied",
    "administration.conference_disabled":
      "adminUsers.audit.action.conferenceDisabled",
    "administration.conference_discovered":
      "adminUsers.audit.action.conferenceDiscovered",
    "administration.conference_registration_requested":
      "adminUsers.audit.action.conferenceRegistrationRequested",
    "administration.conference_registration_activated":
      "adminUsers.audit.action.conferenceRegistrationActivated",
    "administration.conference_management_denied":
      "adminUsers.audit.action.conferenceManagementDenied",
    "administration.conference_branding_uploaded":
      "adminUsers.audit.action.brandingUploaded",
    "administration.conference_branding_removed":
      "adminUsers.audit.action.brandingRemoved",
    "administration.conference_branding_management_denied":
      "adminUsers.audit.action.brandingManagementDenied",
    "financial.sponsorship_created":
      "adminUsers.audit.action.sponsorshipCreated",
    "financial.sponsorship_updated":
      "adminUsers.audit.action.sponsorshipUpdated",
    "financial.sponsorship_invoice_recorded":
      "adminUsers.audit.action.sponsorshipInvoiceRecorded",
    "financial.sponsorship_payment_recorded":
      "adminUsers.audit.action.sponsorshipPaymentRecorded",
    "financial.sponsorship_management_denied":
      "adminUsers.audit.action.sponsorshipManagementDenied",
    "financial.other_revenue_created":
      "adminUsers.audit.action.otherRevenueCreated",
    "financial.other_revenue_updated":
      "adminUsers.audit.action.otherRevenueUpdated",
    "financial.other_revenue_reversed":
      "adminUsers.audit.action.otherRevenueReversed",
    "financial.other_revenue_management_denied":
      "adminUsers.audit.action.otherRevenueManagementDenied",
    "financial.ledger_entry_created":
      "adminUsers.audit.action.ledgerEntryCreated",
    "financial.ledger_entry_reversed":
      "adminUsers.audit.action.ledgerEntryReversed",
  });

const AUDIT_OUTCOME_MESSAGE_KEYS = {
  success: "adminUsers.audit.outcome.success",
  denied: "adminUsers.audit.outcome.denied",
  failed: "adminUsers.audit.outcome.failed",
} as const satisfies Readonly<Record<AuditEventSummary["outcome"], MessageKey>>;

function viewerFromCreation(
  email: string,
  viewer: ViewerSummary,
): ViewerDirectoryItem {
  return { ...viewer, email: email.trim().toLowerCase(), lastSignInAt: null };
}

function errorCopy(error: unknown): LocalizedCopy {
  const code =
    error instanceof AdminUsersApiError ? error.code : "request_failed";
  return { key: ERROR_MESSAGE_KEYS[code] };
}

function errorNotice(error: unknown): LocalizedNotice {
  return { ...errorCopy(error), tone: "error" };
}

function createFailureNotice(
  error: unknown,
  refreshFailed: boolean,
): LocalizedNotice {
  const original = errorCopy(error);
  return refreshFailed
    ? {
        key: "adminUsers.error.withRefreshGuidance",
        nestedMessageKey: original.key,
        tone: "error",
      }
    : { ...original, tone: "error" };
}

function creationNotice(result: ViewerCreationResult): LocalizedNotice {
  if (result.viewer.status === "pending") {
    if (result.viewer.setupDeliveryStatus === "failed") {
      return { key: "adminUsers.error.deliveryFailed", tone: "error" };
    }
    if (result.viewer.setupDeliveryStatus === "not_sent") {
      return { key: "adminUsers.creation.notSent", tone: "warning" };
    }
  }
  if (result.outcome === "created") {
    return { key: "adminUsers.creation.created", tone: "success" };
  }
  if (result.outcome === "pending_setup") {
    return { key: "adminUsers.creation.pending", tone: "warning" };
  }
  if (result.outcome === "already_active") {
    return { key: "adminUsers.creation.active", tone: "neutral" };
  }
  return result.viewer.status === "active"
    ? { key: "adminUsers.creation.recoveredActive", tone: "success" }
    : { key: "adminUsers.creation.recoveredPending", tone: "success" };
}

function viewerStatus(viewer: ViewerSummary): Readonly<{
  key: MessageKey;
  tone: "active" | "pending" | "failed" | "disabled";
}> {
  if (viewer.status === "disabled") {
    return { key: "adminUsers.status.disabled", tone: "disabled" };
  }
  if (viewer.status === "active") {
    return { key: "adminUsers.status.active", tone: "active" };
  }
  return viewer.setupDeliveryStatus === "failed"
    ? { key: "adminUsers.status.deliveryFailed", tone: "failed" }
    : viewer.setupDeliveryStatus === "not_sent"
      ? { key: "adminUsers.status.notSent", tone: "pending" }
      : { key: "adminUsers.status.pending", tone: "pending" };
}

export function AdminUsersPanel({ api, canManageUsers }: AdminUsersPanelProps) {
  const { date, number, t } = useLocale();
  const [email, setEmail] = useState("");
  const [canProvisionViewers, setCanProvisionViewers] = useState(false);
  const [viewers, setViewers] = useState<readonly ViewerDirectoryItem[]>([]);
  const [events, setEvents] = useState<readonly AuditEventSummary[]>([]);
  const [filter, setFilter] = useState<ViewerFilter>("all");
  const [notice, setNotice] = useState<LocalizedNotice>({
    key: "adminUsers.notice.loadingViewers",
    tone: "neutral",
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<LocalizedCopy | null>(null);
  const [createErrorTarget, setCreateErrorTarget] =
    useState<CreateErrorField>("form");
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const createInFlightRef = useRef<string | null>(null);
  const actionInFlightRef = useRef<string | null>(null);
  const drawerRef = useRef<HTMLDialogElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const showProvisioningControl = canShowViewerProvisioning(
    canManageUsers,
    canProvisionViewers,
  );

  useEffect(() => {
    if (!canManageUsers) return;
    const controller = new AbortController();
    const reload = (announce = false) =>
      void api
        .listViewers()
        .then((records) => {
          if (!controller.signal.aborted) {
            setViewers(records);
            if (announce) {
              setNotice({
                key: "adminUsers.notice.directoryReady",
                tone: "neutral",
              });
            }
          }
        })
        .catch((error: unknown) => {
          if (!controller.signal.aborted) {
            setNotice(errorNotice(error));
          }
        });
    reload(true);
    const onUsersChanged = () => reload();
    window.addEventListener(USERS_CHANGED_EVENT, onUsersChanged);
    return () => {
      controller.abort();
      window.removeEventListener(USERS_CHANGED_EVENT, onUsersChanged);
    };
  }, [api, canManageUsers]);

  useEffect(() => {
    let active = true;
    setCanProvisionViewers(false);
    const loadCapability = api.getViewerProvisioningCapability?.bind(api);
    if (!canManageUsers || !loadCapability) return;

    void loadCapability()
      .then((capability) => {
        if (active) {
          setCanProvisionViewers(capability.canProvisionViewers === true);
        }
      })
      .catch(() => {
        if (active) setCanProvisionViewers(false);
      });

    return () => {
      active = false;
    };
  }, [api, canManageUsers]);

  useEffect(() => {
    const drawer = drawerRef.current;
    if (!drawer) return;
    if (drawerOpen && !drawer.open) drawer.showModal();
    if (!drawerOpen && drawer.open) drawer.close();
  }, [drawerOpen]);

  const visibleViewers = useMemo(
    () => viewers.filter((viewer) => matchesViewerFilter(viewer, filter)),
    [filter, viewers],
  );
  const activeCount = viewers.filter(
    (viewer) => viewer.status === "active",
  ).length;
  const pendingCount = viewers.filter(
    (viewer) =>
      viewer.status === "pending" && viewer.setupDeliveryStatus !== "failed",
  ).length;
  const deliveryFailedCount = viewers.filter(
    (viewer) =>
      viewer.status === "pending" && viewer.setupDeliveryStatus === "failed",
  ).length;
  const disabledCount = viewers.filter(
    (viewer) => viewer.status === "disabled",
  ).length;

  if (!canManageUsers) {
    return (
      <section
        className="user-administration user-administration--restricted"
        aria-labelledby="user-administration-title"
      >
        <h2 id="user-administration-title">
          {t("adminUsers.restricted.title")}
        </h2>
        <p>{t("adminUsers.restricted.description")}</p>
      </section>
    );
  }

  function closeCreateDrawer() {
    setEmail("");
    setCreateError(null);
    setCreateErrorTarget("form");
    setDrawerOpen(false);
  }

  async function createViewer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!acquireAction(createInFlightRef, "create-viewer")) {
      return;
    }

    if (!emailInputRef.current?.checkValidity()) {
      setCreateError({ key: "adminUsers.error.invalidEmail" });
      setCreateErrorTarget("email");
      setNotice({
        key: "adminUsers.error.invalidEmail",
        tone: "error",
      });
      releaseAction(createInFlightRef, "create-viewer");
      emailInputRef.current?.focus();
      return;
    }

    setCreating(true);
    setCreateError(null);
    setCreateErrorTarget("form");
    setNotice({ key: "adminUsers.notice.creating", tone: "neutral" });
    try {
      const result = await api.createViewer(email);
      setViewers((current) => {
        const existing = current.find(
          (viewer) => viewer.authUid === result.viewer.authUid,
        );
        const updated = existing
          ? { ...existing, ...result.viewer }
          : viewerFromCreation(email, result.viewer);
        return [
          updated,
          ...current.filter(
            (viewer) => viewer.authUid !== result.viewer.authUid,
          ),
        ];
      });
      closeCreateDrawer();
      setNotice(creationNotice(result));
      window.dispatchEvent(new Event(USERS_CHANGED_EVENT));
    } catch (error) {
      const recovery = await refreshViewersAfterRetryableCreateError(
        api,
        error,
      );
      const failureNotice = createFailureNotice(
        error,
        recovery.kind === "refresh_failed",
      );

      if (recovery.kind === "refreshed") {
        setViewers(recovery.viewers);
        closeCreateDrawer();
      } else {
        setCreateError(failureNotice);
        setCreateErrorTarget(createErrorField(error));
      }
      setNotice(failureNotice);
    } finally {
      releaseAction(createInFlightRef, "create-viewer");
      setCreating(false);
    }
  }

  async function updateAccess(viewer: ViewerDirectoryItem) {
    if (!acquireAction(actionInFlightRef, viewer.authUid)) return;
    setBusyUid(viewer.authUid);
    setNotice({
      key:
        viewer.status === "disabled"
          ? "adminUsers.notice.restoring"
          : "adminUsers.notice.disabling",
      tone: "neutral",
    });
    try {
      const updated =
        viewer.status === "disabled"
          ? await api.resetViewerAccess(viewer.authUid)
          : await api.disableViewer(viewer.authUid);
      setViewers((current) =>
        current.map((candidate) =>
          candidate.authUid === viewer.authUid
            ? { ...candidate, ...updated }
            : candidate,
        ),
      );
      setNotice({
        key:
          updated.status === "disabled"
            ? "adminUsers.notice.disabled"
            : updated.status === "active"
              ? "adminUsers.notice.alreadyActiveAvailable"
              : "adminUsers.notice.pendingRestored",
        tone:
          updated.status === "disabled"
            ? "success"
            : updated.status === "active"
              ? "neutral"
              : "warning",
      });
    } catch (error) {
      setNotice(errorNotice(error));
    } finally {
      releaseAction(actionInFlightRef, viewer.authUid);
      setBusyUid(null);
    }
  }

  async function resetAccess(viewer: ViewerDirectoryItem) {
    if (!acquireAction(actionInFlightRef, viewer.authUid)) return;
    setBusyUid(viewer.authUid);
    setNotice({ key: "adminUsers.notice.resetting", tone: "neutral" });
    try {
      const updated = await api.resetViewerAccess(viewer.authUid);
      setViewers((current) =>
        current.map((candidate) =>
          candidate.authUid === viewer.authUid
            ? { ...candidate, ...updated }
            : candidate,
        ),
      );
      setNotice({
        key:
          updated.status === "active"
            ? "adminUsers.notice.resetSentActive"
            : "adminUsers.notice.resetSentPending",
        tone: "success",
      });
    } catch (error) {
      setNotice(errorNotice(error));
    } finally {
      releaseAction(actionInFlightRef, viewer.authUid);
      setBusyUid(null);
    }
  }

  async function resendSetup(viewer: ViewerDirectoryItem) {
    if (!acquireAction(actionInFlightRef, viewer.authUid)) return;
    setBusyUid(viewer.authUid);
    setNotice({ key: "adminUsers.notice.resending", tone: "neutral" });
    try {
      const result = await api.resendViewerSetup(viewer.authUid);
      setViewers((current) =>
        current.map((candidate) =>
          candidate.authUid === viewer.authUid
            ? { ...candidate, ...result.viewer }
            : candidate,
        ),
      );
      setNotice(
        result.outcome === "already_active"
          ? {
              key: "adminUsers.notice.alreadyComplete",
              tone: "neutral",
            }
          : {
              key: "adminUsers.notice.newSetupSent",
              tone: "success",
            },
      );
    } catch (error) {
      setNotice(errorNotice(error));
    } finally {
      releaseAction(actionInFlightRef, viewer.authUid);
      setBusyUid(null);
    }
  }

  async function loadAuditEvents() {
    setNotice({ key: "adminUsers.notice.loadingAudit", tone: "neutral" });
    try {
      setEvents(await api.listAuditEvents());
      setNotice({ key: "adminUsers.notice.auditLoaded", tone: "success" });
    } catch (error) {
      setNotice(errorNotice(error));
    }
  }

  return (
    <section
      className="user-administration"
      aria-labelledby="user-administration-title"
    >
      <header className="user-administration__header">
        <div>
          <h2 id="user-administration-title">{t("adminUsers.heading")}</h2>
          <p>{t("adminUsers.description")}</p>
        </div>
        {showProvisioningControl ? (
          <button
            className="button button--primary"
            type="button"
            onClick={() => {
              setCreateError(null);
              setCreateErrorTarget("form");
              setDrawerOpen(true);
            }}
          >
            {t("adminUsers.create")}
          </button>
        ) : null}
      </header>

      <section
        className="user-summary"
        aria-label={t("adminUsers.summary.aria")}
      >
        <div>
          <span>{t("adminUsers.summary.active")}</span>
          <strong>{number(activeCount)}</strong>
        </div>
        <div>
          <span>{t("adminUsers.summary.disabled")}</span>
          <strong className="user-summary__danger">
            {number(disabledCount)}
          </strong>
        </div>
        <div>
          <span>{t("adminUsers.summary.pending")}</span>
          <strong className="user-summary__warning">
            {number(pendingCount)}
          </strong>
        </div>
        <div>
          <span>{t("adminUsers.summary.deliveryFailed")}</span>
          <strong className="user-summary__danger">
            {number(deliveryFailedCount)}
          </strong>
        </div>
        <div className="user-summary__filter">
          <label htmlFor="viewer-status-filter">
            {t("adminUsers.filter.label")}
          </label>
          <select
            id="viewer-status-filter"
            value={filter}
            onChange={(event) =>
              setFilter(event.currentTarget.value as ViewerFilter)
            }
          >
            <option value="all">{t("adminUsers.filter.all")}</option>
            <option value="active">{t("adminUsers.filter.active")}</option>
            <option value="pending">{t("adminUsers.filter.pending")}</option>
            <option value="delivery_failed">
              {t("adminUsers.filter.deliveryFailed")}
            </option>
            <option value="disabled">{t("adminUsers.filter.disabled")}</option>
          </select>
        </div>
      </section>

      <p
        className={`user-administration__status user-administration__status--${notice.tone}`}
        role={notice.tone === "error" ? "alert" : "status"}
        aria-live={notice.tone === "error" ? "assertive" : "polite"}
      >
        {notice.nestedMessageKey
          ? t(notice.key, { message: t(notice.nestedMessageKey) })
          : t(notice.key)}
      </p>

      <div className="user-table-wrap">
        <table className="user-table">
          <caption>{t("adminUsers.table.caption")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("adminUsers.table.user")}</th>
              <th scope="col">{t("adminUsers.table.role")}</th>
              <th scope="col">{t("adminUsers.table.status")}</th>
              <th scope="col">{t("adminUsers.table.lastSignIn")}</th>
              <th scope="col">{t("adminUsers.table.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleViewers.map((viewer) => {
              const statusPresentation = viewerStatus(viewer);
              const secondaryAction = viewerSecondaryAction(viewer);
              return (
                <tr key={viewer.authUid} aria-busy={busyUid === viewer.authUid}>
                  <td data-label={t("adminUsers.table.user")}>
                    <strong>
                      <bdi {...bidiIsolationAttributes()}>{viewer.email}</bdi>
                    </strong>
                  </td>
                  <td data-label={t("adminUsers.table.role")}>
                    {t("adminUsers.role.viewer")}
                  </td>
                  <td data-label={t("adminUsers.table.status")}>
                    <span
                      className={`account-status account-status--${statusPresentation.tone}`}
                    >
                      {t(statusPresentation.key)}
                    </span>
                  </td>
                  <td data-label={t("adminUsers.table.lastSignIn")}>
                    {viewer.lastSignInAt
                      ? date(viewer.lastSignInAt, {
                          timeZone: "Asia/Riyadh",
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : t("adminUsers.notSignedIn")}
                  </td>
                  <td data-label={t("adminUsers.table.actions")}>
                    <div className="user-actions">
                      <button
                        className={
                          viewer.status !== "disabled"
                            ? "button button--danger button--compact"
                            : "button button--secondary button--compact"
                        }
                        type="button"
                        disabled={busyUid !== null}
                        onClick={() => void updateAccess(viewer)}
                      >
                        {busyUid === viewer.authUid
                          ? t("adminUsers.action.working")
                          : viewer.status === "disabled"
                            ? t("adminUsers.action.restore")
                            : t("adminUsers.action.disable")}
                      </button>
                      {secondaryAction === "reset_access" ? (
                        <button
                          className="button button--quiet button--compact"
                          type="button"
                          disabled={busyUid !== null}
                          onClick={() => void resetAccess(viewer)}
                        >
                          {busyUid === viewer.authUid
                            ? t("adminUsers.action.resetting")
                            : t("adminUsers.action.reset")}
                        </button>
                      ) : secondaryAction === "resend_setup" ||
                        secondaryAction === "send_setup" ? (
                        <button
                          className="button button--secondary button--compact"
                          type="button"
                          disabled={busyUid !== null}
                          onClick={() => void resendSetup(viewer)}
                        >
                          {busyUid === viewer.authUid
                            ? t("adminUsers.action.resending")
                            : secondaryAction === "send_setup"
                              ? t("adminUsers.action.send")
                              : t("adminUsers.action.resend")}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {visibleViewers.length === 0 ? (
          <p className="user-table__empty">{t("adminUsers.empty")}</p>
        ) : null}
      </div>

      <details className="audit-events">
        <summary>{t("adminUsers.audit.heading")}</summary>
        <button
          className="button button--secondary button--compact"
          type="button"
          onClick={() => void loadAuditEvents()}
        >
          {t("adminUsers.audit.refresh")}
        </button>
        <ol className="audit-events__list">
          {events.map((event) => {
            const actionMessageKey = AUDIT_ACTION_MESSAGE_KEYS[event.action];
            return (
              <li
                key={`${event.occurredAt}-${event.resourceId}-${event.action}`}
              >
                {t(actionMessageKey ?? "adminUsers.audit.action.other")} ·{" "}
                {t(AUDIT_OUTCOME_MESSAGE_KEYS[event.outcome])} ·{" "}
                <time dateTime={event.occurredAt}>
                  {date(event.occurredAt, {
                    timeZone: "Asia/Riyadh",
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </time>
              </li>
            );
          })}
        </ol>
      </details>

      {showProvisioningControl ? (
        <dialog
          ref={drawerRef}
          className="user-drawer"
          aria-labelledby="create-viewer-title"
          onCancel={(event) => {
            event.preventDefault();
            if (!creating) closeCreateDrawer();
          }}
          onClose={closeCreateDrawer}
        >
          <form
            noValidate
            onSubmit={createViewer}
            aria-busy={creating}
            aria-describedby={
              createErrorTarget === "form" && createError
                ? "create-viewer-error"
                : undefined
            }
          >
            <header>
              <div>
                <h2 id="create-viewer-title">{t("adminUsers.create")}</h2>
                <p>{t("adminUsers.drawer.description")}</p>
              </div>
              <button
                className="icon-button"
                type="button"
                aria-label={t("adminUsers.drawer.close")}
                disabled={creating}
                onClick={closeCreateDrawer}
              >
                ×
              </button>
            </header>
            <div className="user-drawer__body">
              <label htmlFor="viewer-email">
                {t("adminUsers.drawer.email")}
              </label>
              <input
                ref={emailInputRef}
                id="viewer-email"
                name="viewer-email"
                type="email"
                autoComplete="email"
                spellCheck={false}
                {...bidiIsolationAttributes()}
                required
                disabled={creating}
                aria-invalid={
                  createErrorTarget === "email" && Boolean(createError)
                }
                aria-describedby={
                  createErrorTarget === "email" && createError
                    ? "create-viewer-error"
                    : undefined
                }
                value={email}
                onChange={(event) => {
                  setEmail(event.currentTarget.value);
                  setCreateError(null);
                  setCreateErrorTarget("form");
                }}
              />
              <p className="form-message">
                {t("adminUsers.drawer.deliveryHint")}
              </p>
              {createError ? (
                <p
                  id="create-viewer-error"
                  className="form-message form-message--error"
                  role="alert"
                >
                  {createError.nestedMessageKey
                    ? t(createError.key, {
                        message: t(createError.nestedMessageKey),
                      })
                    : t(createError.key)}
                </p>
              ) : null}
              <dl>
                <div>
                  <dt>{t("adminUsers.table.role")}</dt>
                  <dd>{t("adminUsers.role.viewer")}</dd>
                </div>
                <div>
                  <dt>{t("adminUsers.drawer.access")}</dt>
                  <dd>{t("adminUsers.drawer.readOnly")}</dd>
                </div>
              </dl>
            </div>
            <footer>
              <button
                className="button button--quiet"
                type="button"
                disabled={creating}
                onClick={closeCreateDrawer}
              >
                {t("adminUsers.drawer.cancel")}
              </button>
              <button
                className="button button--primary"
                type="submit"
                disabled={creating}
              >
                {creating
                  ? t("adminUsers.drawer.sending")
                  : t("adminUsers.drawer.send")}
              </button>
            </footer>
          </form>
        </dialog>
      ) : null}
    </section>
  );
}
